import PropTypes from 'prop-types';
import * as React from 'react';
import { injectIntl } from 'react-intl';
import { PageSpinner } from 'shared-components/decorators/spinner';
import { CheckoutContextProvider } from '../CheckoutContextProvider';
import {
  fetchExistingQuote,
  fetchInitialQuote
} from '../services/checkout-service';
import {
  fetchCustomerBillingAddress,
  fetchCustomerPaymentMethods,
  fetchStripeKey
} from '../services/payment-service';
import { StripeInstanceContextProvider } from '../StripeInstanceContextProvider';
import { BookingRequestErrorDialog } from './BookingRequestErrorDialog';
import { BookingRequestPage } from './BookingRequestPage';

export const BookingRequestPageContainer = injectIntl(
  ({ accessToken, routeParams, searchParams, profile, intl }) => {
    const existingBookingId = searchParams.get('request');
    const [stripeKey, setStripeKey] = React.useState(null);
    const [providerProps, setProviderProps] = React.useState(null);

    const [checkoutInitErrorMsg, setCheckoutInitErrorMsg] =
      React.useState(null);

    // @TODO: currently all data for the page is fetched here
    // This should be refactored to fetch data in the components that need it
    // This requires designs for loading/error states for each section
    const initializeCheckout = async () => {
      try {
        const billingAddressPromise = fetchCustomerBillingAddress(
          accessToken,
          profile?.SSOid
        );
        const quotePromise =
          existingBookingId?.length > 18
            ? fetchExistingQuote(
                accessToken,
                routeParams.listingID,
                existingBookingId
              )
            : fetchInitialQuote(
                accessToken,
                profile.SSOid,
                routeParams.listingID
              );

        const paymentDataPromise = fetchCustomerPaymentMethods(
          accessToken,
          profile.SSOid
        ).catch(() => {
          // catch error separately so page can still load if payment data is not available
          return [];
        });

        const { inventory, quote, listing, error } = await quotePromise;

        const listingCountry = listing?.country;
        const stripeKeyPromise = fetchStripeKey(listingCountry, intl.locale);

        const [
          stripeKey,
          customerPaymentMethods,
          customerBillingAddress
        ] = await Promise.all([
          stripeKeyPromise,
          paymentDataPromise,
          billingAddressPromise,
        ]);

        setStripeKey(stripeKey);
        setProviderProps({
          initialQuote: quote,
          initialError: error,
          initialBookingId: existingBookingId,
          listing,
          inventory,
          customerBillingAddress,
          customerPaymentMethods,
          intl
        });
      } catch (error) {
        setCheckoutInitErrorMsg(
          intl.formatMessage({
            id: 'checkout.generic_error',
            defaultMessage:
              'An error occurred. Please refresh the page and try again.'
          })
        );
      }
    };

    React.useEffect(() => {
      if (!routeParams?.listingID || !profile?.SSOid) {
        return;
      }
      initializeCheckout();
    }, [profile?.SSOid]);

    return (
      <>
        {!stripeKey || !providerProps ? (
          <PageSpinner additionalStyle="gray" />
        ) : (
          <StripeInstanceContextProvider stripeKey={stripeKey} locale={intl?.locale}>
            <CheckoutContextProvider
              accessToken={accessToken}
              SSOid={profile?.SSOid}
              profile={profile}
              {...providerProps}
            >
              <BookingRequestPage
                profile={profile}
                intl={intl}
                accessToken={accessToken}
              />
            </CheckoutContextProvider>
          </StripeInstanceContextProvider>
        )}

        <BookingRequestErrorDialog
          isOpen={!!checkoutInitErrorMsg}
          setIsOpen={() => {
            setCheckoutInitErrorMsg('');
            window.location.reload();
          }}
          errorMsg={checkoutInitErrorMsg}
          showSupportLink={false}
        />
      </>
    );
  }
);

BookingRequestPageContainer.displayName = 'BookingRequestPageContainer';
BookingRequestPageContainer.propTypes = {
  accessToken: PropTypes.string,
  routeParams: PropTypes.object,
  searchParams: PropTypes.object,
  profile: PropTypes.object,
  intl: PropTypes.object
};
